import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import img1 from '../../../assets/attendenceCarousel.png';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import { attendanceApi } from '../services/api';
import { DateRangePicker } from '../components/analytics';

interface RegionData {
  roID: number;
  roName: string;
  totalEnrollment: number;
  totalboysEnrolled: number;
  totalgirlsEnrolled: number;
  totalPresent: number;
  totalBoysPresent: number;
  totalGirlsPresent: number;
  totalAbsent: number;
  totalBoysAbsent: number;
  totalGirlsAbsent: number;
  totalAttendancePercentage: number;
  boysPresentPercentage: number;
  girlsPresentPercentage: number;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function RegionAttendanceList() {
  const carouselImages = [img1];
  const navigate = useNavigate();

  const [regions, setRegions] = useState<RegionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  });

  // Date range state - default to last 7 days
  const getDefaultDateRange = () => {
    const today = new Date();
    const toDate = today.toISOString().split('T')[0];
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 7);
    return {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [orderBy, setOrderBy] = useState<'attendancePercentage' | '1' | '2'>('attendancePercentage');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Helper to get difference in days
  const getDaysDifference = (from: string, to: string) => {
    if (!from || !to) return 0;
    const f = new Date(from);
    const t = new Date(to);
    const diffTime = Math.abs(t.getTime() - f.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const loadRegionData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await attendanceApi.getStudentAttendanceByRegion(
        currentPage,
        dateRange.fromDate,
        dateRange.toDate,
        orderBy,
        order
      );

      if (response && response.success && Array.isArray(response.data)) {
        setRegions(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setRegions([]);
      }
    } catch (error) {
      console.error('Error loading region data:', error);
      setRegions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateRange, orderBy, order]);

  useEffect(() => {
    loadRegionData();
  }, [loadRegionData]);

  const handleRegionClick = (roID: number, roName: string) => {
    // Navigate to KV schools list for this region
    navigate(`/attendance/region/${roID}/schools?name=${encodeURIComponent(roName)}&fromDate=${dateRange.fromDate}&toDate=${dateRange.toDate}`);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatNumber = (num: number) => {
    return num?.toLocaleString() || '0';
  };

  const formatPercentage = (num: number) => {
    return num?.toFixed(2) || '0.00';
  };

  return (
    <div className="min-h-screen flex flex-col gap-0 bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>
      <main className="flex-grow w-full max-w-8xl mx-auto px-4 py-5 md:px-8 lg:px-16">
        <h2 className="text-4xl font-semibold text-[#6A1786] mb-6">Student Attendance by Region</h2>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl border border-purple-100 shadow-sm mb-6">
          {/* Top gradient accent stripe */}
          <div className="h-1 w-full bg-gradient-to-r from-[#6A1786] via-purple-400 to-pink-400 rounded-t-2xl" />
          
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
              {/* Date Range Section */}
              <div className="lg:col-span-2 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-[#6A1786]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 leading-none mb-0.5">Filter by</p>
                    <p className="text-sm font-semibold text-gray-800 leading-none">Date Range</p>
                  </div>
                </div>

                <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                {/* Preset buttons */}
                <div className="flex items-center gap-2">
                  {[7, 14, 30].map(days => {
                    const diff = getDaysDifference(dateRange.fromDate, dateRange.toDate);
                    const isActive = diff === days;
                    return (
                      <button
                        key={days}
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const pastDate = new Date(today);
                          pastDate.setDate(today.getDate() - days);
                          setDateRange({
                            fromDate: pastDate.toISOString().split('T')[0],
                            toDate: today.toISOString().split('T')[0]
                          });
                          setCurrentPage(1);
                        }}
                        className={`relative px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                          isActive
                            ? 'bg-[#6A1786] text-white shadow-md shadow-purple-200 scale-105'
                            : 'bg-gray-100 text-gray-500 hover:bg-purple-50 hover:text-purple-700 hover:scale-105'
                        }`}
                      >
                        {days === 7 ? '7 Days' : days === 14 ? '2 Weeks' : '1 Month'}
                        {isActive && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-pink-400 rounded-full animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="h-8 w-px bg-gray-200 hidden sm:block" />

                {/* Custom Picker */}
                <DateRangePicker
                  value={dateRange}
                  onSubmit={(range) => {
                    if (range.fromDate && range.toDate) {
                      setDateRange({
                        fromDate: range.fromDate,
                        toDate: range.toDate
                      });
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>

              {/* Sorting Section */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 lg:justify-end">
                <div className="w-full sm:w-auto min-w-[150px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Sort By</label>
                  <select
                    value={orderBy}
                    onChange={(e) => {
                      setOrderBy(e.target.value as any);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786] bg-white text-gray-800 font-medium"
                  >
                    <option value="attendancePercentage">Attendance %</option>
                    <option value="1">Region Name</option>
                    <option value="2">Enrollment</option>
                  </select>
                </div>
                <div className="w-full sm:w-auto min-w-[150px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Order</label>
                  <select
                    value={order}
                    onChange={(e) => {
                      setOrder(e.target.value as 'ASC' | 'DESC');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786] bg-white text-gray-800 font-medium"
                  >
                    <option value="DESC">Descending</option>
                    <option value="ASC">Ascending</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Region Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#481789] to-[#6A1786] text-white">
                  <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Region Name</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Total Enrollment</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Boys %</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Girls %</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Loading region data...
                    </td>
                  </tr>
                ) : regions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No region data available
                    </td>
                  </tr>
                ) : (
                  regions.map((region, idx) => (
                    <tr 
                      key={region.roID} 
                      className="border-b hover:bg-purple-50 cursor-pointer transition-colors"
                      onClick={() => handleRegionClick(region.roID, region.roName)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {(currentPage - 1) * pagination.limit + idx + 1}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-[#6A1786] hover:underline">
                        {region.roName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">
                        {formatNumber(region.totalEnrollment)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">
                        {formatPercentage(region.boysPresentPercentage)}%
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">
                        {formatPercentage(region.girlsPresentPercentage)}%
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          region.totalAttendancePercentage >= 90 
                            ? 'bg-green-100 text-green-800' 
                            : region.totalAttendancePercentage >= 75 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {formatPercentage(region.totalAttendancePercentage)}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && regions.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-[#F5E6FA] border-t">
              <div className="text-sm text-gray-700">
                Showing {(currentPage - 1) * pagination.limit + 1} to{' '}
                {Math.min(currentPage * pagination.limit, pagination.total)} of{' '}
                {pagination.total} regions
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm text-[#6A1786] hover:bg-purple-100 rounded disabled:text-gray-400 disabled:hover:bg-transparent"
                >
                  &lt;&lt; Previous
                </button>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-700">Page</span>
                  <input
                    type="number"
                    value={currentPage}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) {
                        handlePageChange(val);
                      }
                    }}
                    min={1}
                    max={pagination.totalPages}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
                  />
                  <span className="text-sm text-gray-700">of {pagination.totalPages}</span>
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-1 text-sm text-[#6A1786] hover:bg-purple-100 rounded disabled:text-gray-400 disabled:hover:bg-transparent"
                >
                  Next &gt;&gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
